import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import axios from "axios";
import {
  Typography,
  Box,
  Paper,
  Chip,
  Grid,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Button,
  useTheme,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditLicenseDialog from "../dialogs/EditLicenseDialog";
import { getTimeNoUTCNoTime } from '../common/dateUtils';
import { getTimeNoUTC } from '../common/dateUtils';
import { EnhancedTable, stableSort, getSorting } from "../common/EnhancedTable";


function TabPanel({ children, value, index }) {
  return value === index && <Box sx={{ pt: 3, px: 0 }}>{children}</Box>;
}

export default function LicenseDetails({ authData, userData }) {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const { id } = useParams();
  const navigate = useNavigate();
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hasUpdatePermission, setHasUpdatePermission] = useState(false);
  const [fieldToEdit, setFieldToEdit] = useState(null);

  const [licenseHistory, setLicenseHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyTotalElements, setHistoryTotalElements] = useState(0);
  const [historyPagination, setHistoryPagination] = useState({
    order: 'asc',
    orderBy: 'updatedAt',
    page: 0,
    rowsPerPage: 10
  });

  const saveHistoryPaginationState = (obj) => {
    setHistoryPagination({
      ...historyPagination,
      order: obj.order,
      orderBy: obj.orderBy,
      page: obj.page,
      rowsPerPage: obj.rowsPerPage,
    });
  };

  useEffect(() => {
    if (license && authData) {
      const hasPermission = authData.permission?.includes('update-license');
      
      const isOrgAdmin = license.orgId && 
        authData.authorizedGroups?.some(group => 
          group === `${license.orgId}_admin`
        );
        
      setHasUpdatePermission(hasPermission && isOrgAdmin);
    }
  }, [license, authData]);

  const getLicenseHistory = () => {
    setHistoryLoading(true);
    setHistoryError(null);

    const url = `/api/licenses/${id}/history`;
    const headers = {
      "Content-Type": "application/json"
    };

    const params = {
      page: historyPagination.page,
      size: historyPagination.rowsPerPage,
      sort: "desc",
    };

    return axios({
      url: url,
      method: "GET",
      headers: headers,
      params: params
    })
      .then(response => {
        setLicenseHistory(response.data.content || []);
        setHistoryTotalElements(response.data.page.totalElements);
        setHistoryLoading(false);
      })
      .catch(error => {
        console.error("Error fetching license history:", error);
        setHistoryError("Failed to load license history.");
        setHistoryLoading(false);
      });
  };

  useEffect(() => {
    if (tabValue === 1 && id) {
      getLicenseHistory();
    }
    // eslint-disable-next-line
  }, [tabValue, id, historyPagination.page, historyPagination.rowsPerPage]);

  const handleDialogOpen = (field) => {
    setFieldToEdit(field);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const refreshLicenseData = () => {
    setLoading(true);
    axios
      .get(`/api/licenses/${id}`)
      .then((response) => {
        setLicense(response.data);
        setLoading(false);
        if (tabValue === 1) {
          getLicenseHistory();
        }
      })
      .catch((error) => {
        console.error("Error fetching license details:", error);
        setLoading(false);
      });
  };

  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const formatPerson = (person) => {
    if (!person) return '';
    const { firstName = '', lastName = '', userName = '' } = person;
    return `${firstName} ${lastName} - ${userName}`;
  };

  const getOwnerName = () => {
    const firstName = capitalize(license.ownerFirstName);
    const lastName = capitalize(license.ownerLastName);
  
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return 'N/A';
  };


  useEffect(() => {
    axios
      .get(`/api/licenses/${id}`)
      .then((response) => {
        setLicense(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching license details:", error);
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <Backdrop open={true} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (!license) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h5" color="error">
          License not found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>

      {/* Main Content */}
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3, color: 'text.primary' }}
          onClick={() => navigate("/")}
        >
          Back to search
        </Button>

        {/* License Header */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
          <Chip
            label="License"
            sx={{
              bgcolor: theme.palette.primary.main,
              color: 'white',
              fontWeight: 'bold',
              borderRadius: 1,
            }}
          />
          <Typography sx={{ color: 'black' }}>
            {license.id}
          </Typography>
          <Chip
            label={license.status}
            sx={{
              bgcolor: '#FFE082',
              color: 'text.primary',
              fontWeight: 'bold',
            }}
          />
        </Box>

        {/* License Info Card */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">Start Date</Typography>
                <Typography>{license.startsAt}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">End Date</Typography>
                <Typography>{license.endsAt}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography>{license.duration}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography>{license.status}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">License Type</Typography>
                <Typography>{license.type}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">Entitlement Type</Typography>
                <Typography>{license.entitlementType}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Redeemed Entitlements</Typography>
                <Typography>{license.numEntitlements}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Max Entitlements</Typography>
                <Typography>{license.maxEntitlements}</Typography>
              </Box>
              <Box sx={{ mt: 2}}>
                <Typography variant="caption" color="text.secondary">Remaining Entitlements</Typography>
                <Typography>{license.maxEntitlements - license.numEntitlements}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">Owner Name</Typography>
                <Typography>{getOwnerName()}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Owner Username</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography>{license.ownerUsername || 'N/A'}</Typography>
                  {hasUpdatePermission && (
                    <EditIcon
                      sx={{
                        ml: 1,
                        fontSize: 18,
                        color: theme.palette.primary.main,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleDialogOpen('ownerUsername')}
                    />
                  )}
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Owner Email</Typography>
                <Typography>{license.ownerEmail || 'N/A'}</Typography>
              </Box>
              <Box sx = {{ mt : 2}}>
                <Typography variant="caption" color="text.secondary">Auto Renew</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography>{license.autoRenew ? license.autoRenew : "N/A"}</Typography>
                  {hasUpdatePermission && (
                    <EditIcon
                      sx={{
                        ml: 1,
                        fontSize: 18,
                        color: theme.palette.primary.main,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleDialogOpen('autoRenew')}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs and Content */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Additional Detail" />
            <Tab label="History" />
            <Tab label="Products" />
            <Tab label="Personnel" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Table>
            <TableBody>
                <TableRow>
                    <TableCell sx={{ width: '20%' }}>Order ID:</TableCell>
                    <TableCell sx = {{ width: '80%' }}>{license.orderId}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell sx={{ width: '20%' }}>Order System:</TableCell>
                    <TableCell sx={{ width: '80%' }}>{license.orderSystem}</TableCell>
                </TableRow>
            </TableBody>
          </Table>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : historyError ? (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Typography variant="h6" color="error">
                {historyError}
              </Typography>
            </Box>
          ) : licenseHistory.length === 0 ? (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No history available
              </Typography>
            </Box>
          ) : (
            <EnhancedTable
              headCells={[
                { id: "updatedAt", label: "Action Date" },
                { id: "updatedFor", label: "Updated By" },
                { id: "status", label: "Status" },
                { id: "numEntitlements", label: "Redeemed Entitlements" },
                { id: "autoRenew", label: "Auto Renew" },
                { id: "ownerId", label: "Owner ID" },
                { id: "endsAt", label: "End Date" },
              ]}
              order={historyPagination.order}
              orderBy={historyPagination.orderBy}
              count={historyTotalElements}
              page={historyPagination.page}
              rowsPerPage={historyPagination.rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              savePaginationState={saveHistoryPaginationState}
            >
              <TableBody>
                {stableSort(
                  licenseHistory,
                  getSorting(historyPagination.order, historyPagination.orderBy)
                )
                  .map((historyItem, index) => (
                    <TableRow key={index}>
                      <TableCell>{getTimeNoUTC(historyItem.updatedAt) || 'N/A'}</TableCell>
                      <TableCell>{historyItem.updatedFor ? historyItem.updatedFor
                      : historyItem.updatedBy ? historyItem.updatedBy
                      : 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={historyItem.status}
                          size="small"
                          sx={{
                            bgcolor: historyItem.status === 'ACTIVE' ? '#4caf50' :
                                    historyItem.status === 'EXPIRED' ? '#f44336' :
                                    historyItem.status === 'PENDING' ? '#ff9800' : '#9e9e9e',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell>{historyItem.numEntitlements ?? 'N/A'}</TableCell>
                      <TableCell>{historyItem.autoRenew || 'N/A'}</TableCell>
                      <TableCell>{historyItem.ownerId || 'N/A'}</TableCell>
                      <TableCell>{getTimeNoUTCNoTime(historyItem.endsAt) || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </EnhancedTable>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Products Tab */}
          {license.products && license.products.length > 0 ? (
            <EnhancedTable
                headCells={[
                { id: "name", label: "Product Name" },
                { id: "platform", label: "Platform" },
                { id: "model", label: "Product Model" },
                { id: "isbn", label: "ISBN" },
                ]}
                order="asc"
                orderBy="name"
                count={license.products?.length}
                page={0}
                rowsPerPage={10}
                rowsPerPageOptions={[5, 10, 25]}
                savePaginationState={() => {}}
            >
              <TableBody>
                {license.products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>{product.name || 'N/A'}</TableCell>
                    <TableCell>{product.platform || 'N/A'}</TableCell>
                    <TableCell>{product.model || 'N/A'}</TableCell>
                    <TableCell>{product.isbn || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </EnhancedTable>
          ) : (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No products available
              </Typography>
            </Box>
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
            {/* Personnel Tab */}
            <Table>
                <TableHead>
                <TableRow>
                    <TableCell  
                    sx={{
                        width: '50%',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        bgcolor: 'background.secondary',
                        color: 'black',
                        borderRight: '1px solid #ddd',
                    }}>
                    Administrators
                    </TableCell>
                    <TableCell
                    sx={{
                        width: '50%',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        bgcolor: 'background.secondary',
                        color: 'black',
                    }}>
                    Contacts
                    </TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                      <TableCell sx={{ textAlign: 'center', borderRight: '1px solid #ddd', }}>
                        {license.administrators && license.administrators.length > 0 
                        ? license.administrators.map((admin, index) => (
                          <Box key={index} sx={{ mb: index < license.administrators.length - 1 ? 1 : 0 }}>
                            {formatPerson(admin)}
                          </Box>
                          ))
                        : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {license.contacts && license.contacts.length > 0
                        ? license.contacts.map((contact, index) => (
                          <Box key={index} sx={{ mb: index < license.contacts.length - 1 ? 1 : 0 }}>
                            {formatPerson(contact)}
                          </Box>
                          ))
                        : 'N/A'}
                      </TableCell>
                </TableRow>
              </TableBody>
            </Table>
        </TabPanel>
      </Box>
      <EditLicenseDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fieldToEdit={fieldToEdit}
        license={license}
        onUpdate={refreshLicenseData}
        userData={userData}
         />
    </Box>
  );
}